import { ClaudeApiKeySection } from '@/components/ClaudeApiKeySection'
import {
  Avatar,
  AvatarFallback,
  Card,
  Field,
  FieldGroup,
  FieldLabel,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { useTenant } from '@/contexts/TenantContext'
import {
  useDeleteUserClaudeToken,
  useSaveUserClaudeToken,
  useUserClaudeToken,
} from '@/hooks'
import { useAuth, useCurrentUser } from '@/hooks/useAuth'
import { Permissions } from '@/lib/permissions'
import { getInitials } from '@/lib/utils'
import { useMsal } from '@azure/msal-react'
import { Building2, Moon, Sun } from 'lucide-react'
import { useTheme } from 'next-themes'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'

export function SettingsPage() {
  const navigate = useNavigate()
  const { accounts } = useMsal()
  const { data: user } = useCurrentUser()
  const { hasPermission } = useAuth()
  const { currentTenant, availableTenants, setCurrentTenant } = useTenant()

  const canManageClaudeToken = hasPermission(Permissions.Host.User.Settings.ManageClaudeToken)
  const { data: tokenData, isLoading: isTokenLoading } = useUserClaudeToken(canManageClaudeToken)
  const saveToken = useSaveUserClaudeToken()
  const deleteToken = useDeleteUserClaudeToken()

  const firstName = user?.firstName ?? ''
  const lastName = user?.lastName ?? ''
  const fullName = firstName && lastName ? `${firstName} ${lastName}` : user?.displayName ?? ''
  const initials = firstName && lastName
    ? `${firstName[0]}${lastName[0]}`.toUpperCase()
    : fullName ? getInitials(fullName) : ''
  const activeTenants = availableTenants.filter(t => t.status === 'Active')
  const isSingleTenant = activeTenants.length <= 1

  const handleClose = () => {
    navigate(-1)
  }

  return (
    <Dialog open onOpenChange={(open) => { if (!open) handleClose() }}>
      <DialogContent className="max-w-[100vw] w-[100vw] h-[100vh] max-h-[100vh] flex flex-col p-0 rounded-none border-none gap-0">
        <DialogHeader className="px-8 py-4 border-b shrink-0 bg-background">
          <DialogTitle className="text-xl font-bold">User Settings</DialogTitle>
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-y-auto bg-input-background">
          <div className="max-w-5xl w-full mx-auto px-6 py-8 space-y-6">
            <Card className="p-8">
              <Avatar className="h-14 w-14 mb-4">
                <AvatarFallback className="bg-primary text-primary-foreground text-lg font-semibold">
                  {initials}
                </AvatarFallback>
              </Avatar>

              <h2 className="text-xl font-bold mb-6">Profile</h2>

              <FieldGroup>
                <div className="grid grid-cols-2 gap-4">
                  <Field>
                    <FieldLabel htmlFor="firstName">Name</FieldLabel>
                    <Input id="firstName" value={firstName} disabled readOnly />
                  </Field>
                  <Field>
                    <FieldLabel htmlFor="lastName">Surname</FieldLabel>
                    <Input id="lastName" value={lastName} disabled readOnly />
                  </Field>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <Field>
                    <FieldLabel htmlFor="email">Email Address</FieldLabel>
                    <Input id="email" type="email" value={user?.email || accounts[0]?.username || ''} disabled readOnly />
                  </Field>
                  <Field>
                    <FieldLabel htmlFor="role">Tenant Role</FieldLabel>
                    <Input id="role" value={currentTenant?.role ?? ''} disabled readOnly />
                  </Field>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <Field>
                    <FieldLabel htmlFor="tenant">Tenant</FieldLabel>
                    <Select
                      value={currentTenant?.id ?? ''}
                      onValueChange={(id) => {
                        const tenant = activeTenants.find(t => t.id === id)
                        if (tenant) setCurrentTenant(tenant)
                      }}
                      disabled={isSingleTenant}
                    >
                      <SelectTrigger id="tenant">
                        <div className="flex items-center gap-2">
                          <Building2 className="h-4 w-4 shrink-0" />
                          <SelectValue placeholder="No tenant" />
                        </div>
                      </SelectTrigger>
                      <SelectContent>
                        {activeTenants.map(t => (
                          <SelectItem key={t.id} value={t.id}>
                            {t.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                </div>
              </FieldGroup>
            </Card>

            {canManageClaudeToken && (
              <Card className="p-8 pt-0">
                <ClaudeApiKeySection
                  title="Claude OAuth Token"
                  description="Your personal Claude OAuth token. When set, it is used in every project before any Anthropic API key: the one you connect on a project's Connectors page, the project key and the organization key."
                  fallbackInfo={
                    <p className="rounded-md border bg-muted/50 p-3 text-sm text-muted-foreground" data-testid="user-ai-provider-connectors-note">
                      API keys for Anthropic, GitHub Copilot and Qubiz Titan are set per project, on each project's Connectors page.
                    </p>
                  }
                  hasKey={tokenData?.hasToken ?? false}
                  isLoading={isTokenLoading}
                  isSaving={saveToken.isPending}
                  isDeleting={deleteToken.isPending}
                  onSave={async (token) => {
                    await saveToken.mutateAsync(token)
                    toast.success('Claude OAuth token saved.')
                  }}
                  onDelete={async () => {
                    await deleteToken.mutateAsync()
                    toast.success('Claude OAuth token removed.')
                  }}
                  onCancel={handleClose}
                  testIdPrefix="user-claude-token"
                  credentialNoun="OAuth token"
                  credentialNounLabel="OAuth Token"
                  helpContent={null}
                />
              </Card>
            )}

            <Card className="p-8">
              <h2 className="text-xl font-bold mb-6">Interface & Theme</h2>
              <p className="mb-4 text-sm text-muted-foreground">
                Select your interface color scheme.
              </p>
              <ThemeSwitcher />
            </Card>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export function ThemeSwitcher() {
  const theme = useTheme()
  return ( 
  <div className="inline-flex border-1 border-primary rounded-lg p-1 gap-2">
    <button className={`flex p-1 gap-1 items-center border-1 rounded-lg ${theme.resolvedTheme === 'light' ? 'bg-primary text-primary-foreground' : ''}`} onClick={() => theme.setTheme('light')}>
      <Sun className="h-4 w-4" />
      Light
    </button>
    <button className={`flex p-1 gap-1 items-center border-1 rounded-lg ${theme.resolvedTheme === 'dark' ? 'bg-primary text-primary-foreground' : ''}`} onClick={() => theme.setTheme('dark')}>
      <Moon className="h-4 w-4" />
      Dark
    </button>
  </div>)
}
